import { Box } from '@mui/material';
import React from 'react';

import { customSizes, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize, {
	getScreenSizeCategory,
	type ScreenSizeCategory,
} from '../../../utils/useScreenSize';
import AIAssistantTemplatePromptForm from '../shared/TemplatePromptForm/AIAssistantTemplatePromptForm';
import AIToolsFormHeader from './AIToolsFormHeader';
import AIToolsFormStickyFooter from './AIToolsFormStickyFooter';

const OUTER_PADDING: Record<ScreenSizeCategory, string> = {
	small: `0 ${spaces.md}`,
	medium: `${spaces.md} ${spaces.md} 0 ${spaces.md}`,
	large: `${spaces.md} ${spaces.xxxxxl} 0 ${spaces.xxxxxl}`,
};

const INNER_PADDING: Record<ScreenSizeCategory, string> = {
	small: `${spaces.lg} ${spaces.md} ${spaces.lg} ${spaces.md}`,
	medium: `${spaces.md} ${spaces.md} ${spaces.lg} ${spaces.md}`,
	large: `${spaces.md} ${spaces.xxxl} ${spaces.lg} ${spaces.xxxl}`,
};

const getFormBoxStyles = (screenSize: ScreenSizeCategory) => ({
	display: 'flex',
	maxWidth: customSizes.aiAssistantForm.containerMaxWidth,
	width: '100%',
	padding: OUTER_PADDING[screenSize],
	flexDirection: 'column',
	alignItems: 'flex-start',
	flex: '1 0 0',
	alignSelf: 'stretch',
	borderRadius: 0,
	margin: '0 auto',
	overflow: 'visible',
	boxSizing: 'border-box',
});

const getInnerFormBoxStyles = (screenSize: ScreenSizeCategory) => ({
	display: 'flex',
	width: '100%',
	maxWidth: customSizes.aiAssistantForm.innerMaxWidth,
	padding: INNER_PADDING[screenSize],
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: spaces.lg,
	flex: '1 0 0',
	alignSelf: 'stretch',
	overflow: 'visible',
	boxSizing: 'border-box',
});

export const createContentWrapper =
	(innerFormBoxStyles: Record<string, unknown>) =>
	(children: React.ReactNode, title: string, description: string) => (
		<Box sx={innerFormBoxStyles}>
			<AIToolsFormHeader title={title} description={description} />
			{children}
		</Box>
	);

const PageTemplatePromptForm: React.FC = () => {
	const { isXxsScreen, isXsScreen, isSmScreen } = useScreenSize();
	const screenSize = getScreenSizeCategory(isXxsScreen, isXsScreen, isSmScreen);
	const formBoxStyles = getFormBoxStyles(screenSize);
	const innerFormBoxStyles = getInnerFormBoxStyles(screenSize);

	const renderFooter = (props: {
		onCancel: () => void;
		onSubmit: () => void;
		disabled: boolean;
	}) => (
		<AIToolsFormStickyFooter
			onCancel={props.onCancel}
			onSubmit={props.onSubmit}
			cancelDisabled={props.disabled}
			submitDisabled={props.disabled}
		/>
	);

	const renderDescription = (description: string) => (
		<AIToolsFormHeader
			title="" // title is handled separately in the content
			description={description}
		/>
	);

	const contentStyles = {
		position: 'relative',
		padding: 0,
		background: 'transparent',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'visible',
		width: '100%',
		boxSizing: 'border-box',
	};

	const renderWrapper = (children: React.ReactNode, footer: React.ReactNode) => (
		<>
			<Box sx={formBoxStyles}>{children}</Box>
			{footer}
		</>
	);

	const renderContentWrapper = createContentWrapper(innerFormBoxStyles);

	return (
		<AIAssistantTemplatePromptForm
			renderFooter={renderFooter}
			renderDescription={renderDescription}
			renderWrapper={renderWrapper}
			renderContentWrapper={renderContentWrapper}
			contentStyles={contentStyles}
		/>
	);
};

export default PageTemplatePromptForm;
